import React, { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  useTheme,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  AccessTime as TimeIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ShoppingCart as CartIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { ApiEndPoints, IMAGE_URL } from "src/network/endpoints";
import prepTimeIcon from "src/assets/images/prep-time-icon.png";
import { toast } from "react-hot-toast";
import { axiosInstance } from "src/network/adapter";
import currencyData from "src/constants/currencyData.json";
import { getCurrentlyOpenMenus } from "./menuScheduleUtils";

// Styled Components - Consolidated and optimized
const CustomOverlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10000, // Higher than menu button z-index (9997)
  pointerEvents: "none",
});

const CustomDialog = styled(Box)(({ theme }) => ({
  borderRadius: 20,
  maxWidth: "95vw",
  maxHeight: "95vh",
  width: "900px",
  height: "700px",
  position: "fixed",
  overflow: "hidden",
  background: "white",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  pointerEvents: "auto",
  [theme.breakpoints.down("md")]: { width: "90vw", height: "80vh" },
  [theme.breakpoints.down("sm")]: {
    width: "95vw",
    height: "80vh",
    borderRadius: 16,
  },
  [theme.breakpoints.down(400)]: {
    width: "90vw",
    height: "80vh",
    borderRadius: 12,
  },
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: 25,
  height: 25,
  cursor: "se-resize",
  background:
    "linear-gradient(135deg, rgba(224,224,224,0.9) 0%, rgba(189,189,189,0.9) 100%)", // subtle gray gradient
  borderTopLeftRadius: 20,
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 2,
    right: 2,
    width: 15,
    height: 15,
    background: `repeating-linear-gradient(
      45deg, 
      rgba(158,158,158,0.8), 
      rgba(158,158,158,0.8) 2px, 
      transparent 2px, 
      transparent 4px
    )`, // darker gray stripes
  },
  [theme.breakpoints.down("sm")]: {
    width: 20,
    height: 20,
    borderTopLeftRadius: 16,
    "&::before": {
      bottom: 1,
      right: 1,
      width: 12,
      height: 12,
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 24px",
  background: "white",
  position: "relative",
  zIndex: 10,
  cursor: "move",
  userSelect: "none",
  [theme.breakpoints.down("sm")]: { padding: "12px 16px" },
  [theme.breakpoints.down(400)]: { padding: "10px 12px" },
}));

const DragHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "50%",
  top: 8,
  transform: "translateX(-50%)",
  width: 40,
  height: 4,
  backgroundColor: "#E2E8F0",
  borderRadius: 2,
  cursor: "move",
  "&:hover": { backgroundColor: "#CBD5E1" },
  [theme.breakpoints.down("sm")]: { width: 32, height: 3, top: 6 },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  background: "white",
  borderRadius: 16,
  height: "calc(100% - 100px)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: "0 24px",
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px",
    height: "calc(100% - 80px)",
    borderRadius: 12,
  },
  [theme.breakpoints.down(400)]: {
    padding: "0 12px",
    height: "calc(100% - 70px)",
    borderRadius: 8,
  },
}));

const ScrollableContainer = styled(Box)({
  flex: 1,
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "#F1F5F9", borderRadius: 10 },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1", borderRadius: 10,
    "&:hover": { background: "#94A3B8" },
  },
});

const StickyHeader = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: "white",
  zIndex: 10,
  paddingTop: "6px",
  paddingBottom: "4px",
  borderBottom: "1px solid #E2E8F0",
  [theme.breakpoints.down("sm")]: { paddingTop: "4px", paddingBottom: "3px" },
  [theme.breakpoints.down(400)]: { paddingTop: "3px", paddingBottom: "2px" },
}));

// Shared transition and hover effects
const commonTransition = "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
const primaryColor = "#6200EE";
const primaryLight = "#F2E9FF";

const SearchContainer = styled(Box)(({ theme }) => ({
  marginBottom: "12px",
  [theme.breakpoints.down("sm")]: { marginBottom: "8px" },
  [theme.breakpoints.down(400)]: { marginBottom: "6px" },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#F8FAFC",
    fontSize: "0.9rem",
    "& fieldset": {
      borderColor: "#E2E8F0",
    },
    "&:hover fieldset": {
      borderColor: "#6200EE",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#6200EE",
    },
  },
  "& .MuiInputBase-input": {
    padding: "10px 14px",
    textTransform: "lowercase",
    "&::placeholder": {
      textTransform: "lowercase",
      opacity: 0.7,
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: "0.85rem",
    },
    "& .MuiInputBase-input": {
      padding: "8px 12px",
    },
  },
  [theme.breakpoints.down(400)]: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: "0.8rem",
    },
    "& .MuiInputBase-input": {
      padding: "6px 10px",
    },
  },
}));

const HeroSection = styled(Box)(({ theme, backgroundImage }) => ({
  position: "relative",
  height: 180,
  borderRadius: "12px",
  marginBottom: "4px",
  backgroundImage: backgroundImage
    ? `url(${backgroundImage})`
    : "linear-gradient(135deg, #6200EE 0%, #7C4DFF 100%)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  overflow: "hidden",
  display: "flex",
  alignItems: "flex-end",
  padding: "24px",
  color: "white",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(135deg, rgba(128, 128, 128, 0.6) 0%, rgba(192, 192, 192, 0.3) 100%)",
    zIndex: 1,
  },
  "& > *": { position: "relative", zIndex: 2 },
  [theme.breakpoints.down("sm")]: {
    height: 120,
    padding: "16px",
    borderRadius: "8px",
  },
  [theme.breakpoints.down(400)]: {
    height: 100,
    padding: "12px",
    borderRadius: "6px",
  },
}));

const MenuTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTab-root": {
    textTransform: "lowercase",
    fontWeight: 600,
    fontSize: "1rem",
    minHeight: 46,
    padding: "8px 16px",
    borderRadius: "12px 12px 0 0",
    marginRight: 4,
    color: "#64748B",
    transition: commonTransition,
    "&.Mui-selected": {
      color: primaryColor,
      backgroundColor: primaryLight,
      fontWeight: 700,
    },
    "&:hover": { backgroundColor: primaryLight, color: primaryColor },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
      minHeight: 38,
      padding: "6px 12px",
      borderRadius: "8px 8px 0 0",
    },
    [theme.breakpoints.down(400)]: {
      padding: "4px 8px",
      fontSize: "0.75rem",
      minHeight: 36,
      borderRadius: "6px 6px 0 0",
    },
  },
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTabs-flexContainer": { borderBottom: "2px solid #E2E8F0" },
  [theme.breakpoints.down("sm")]: { minHeight: 36 },
  [theme.breakpoints.down(400)]: { minHeight: 32 },
}));

const CategoryChips = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 12,
  padding: "8px 0",
  overflowX: "auto",
  "&::-webkit-scrollbar": { display: "none" },
  scrollbarWidth: "none",
  [theme.breakpoints.down("sm")]: { gap: 8, padding: "6px 0" },
  [theme.breakpoints.down(400)]: { gap: 6, padding: "4px 0" },
}));

const CategoryChip = styled(Chip)(({ active, theme }) => ({
  height: 44,
  fontSize: "0.95rem",
  fontWeight: active ? 700 : 500,
  backgroundColor: active ? primaryColor : primaryLight,
  color: active ? "white" : primaryColor,
  border: active ? "none" : "2px solid #E2E8F0",
  transition: commonTransition,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: active ? "#5A00D6" : primaryColor,
    color: "white",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px -8px rgba(98, 0, 238, 0.4)",
  },
  "& .MuiChip-label": { padding: "0 20px", textTransform: "lowercase" },
  [theme.breakpoints.down("sm")]: {
    height: 36,
    fontSize: "0.85rem",
    "& .MuiChip-label": { padding: "0 14px" },
  },
  [theme.breakpoints.down(400)]: {
    height: 32,
    fontSize: "0.8rem",
    "& .MuiChip-label": { padding: "0 12px" },
  },
}));

const MenuItemCard = styled(Card)(({ theme }) => ({
  marginBottom: 20,
  borderRadius: 16,
  boxShadow: "0 4px 20px -4px rgba(0,0,0,0.1)",
  transition: commonTransition,
  border: "1px solid #F1F5F9",
  overflow: "hidden",
  "&:hover": {
    boxShadow: "0 12px 40px -8px rgba(0,0,0,0.15)",
    transform: "translateY(-4px)",
    borderColor: "#E2E8F0",
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: 16,
    borderRadius: 12,
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  [theme.breakpoints.down(400)]: {
    marginBottom: 12,
    borderRadius: 10,
  },
}));

const MenuItemContent = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 20,
  padding: 16,
  [theme.breakpoints.down("sm")]: { gap: 12, padding: 12 },
  [theme.breakpoints.down(400)]: { gap: 10, padding: 10 },
}));

const ItemImageContainer = styled(Box)(({ theme }) => ({
  width: 100,
  height: 100,
  borderRadius: 12,
  overflow: "hidden",
  flexShrink: 0,
  backgroundColor: "#F8FAFC",
  position: "relative",
  boxShadow: "0 4px 12px -2px rgba(0,0,0,0.1)",
  [theme.breakpoints.down("sm")]: {
    width: 80,
    height: 80,
    borderRadius: 8,
  },
  [theme.breakpoints.down(400)]: {
    width: 70,
    height: 70,
    borderRadius: 6,
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  bottom: 8,
  right: 8,
  minWidth: '0px !important',
  borderRadius: "50%",
  padding: 6, // ✅ gives breathing space around the icon
  background: "linear-gradient(135deg, #6200EE 0%, #7C4DFF 100%)",
  color: "white",
  boxShadow: "0 4px 12px -2px rgba(98, 0, 238, 0.4)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.1)",
    boxShadow: "0 8px 20px -4px rgba(98, 0, 238, 0.6)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: 4, // smaller padding on mobile
    bottom: 6,
    right: 6,
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  },
  [theme.breakpoints.down(400)]: {
    padding: 3,
    bottom: 4,
    right: 4,
    "& .MuiSvgIcon-root": {
      fontSize: "1.2rem",
    },
  },
}));


const MenuItemsContainer = styled(Box)(({ theme }) => ({
  padding: "12px 0",
  [theme.breakpoints.down("sm")]: { padding: "8px 0" },
  [theme.breakpoints.down(400)]: { padding: "6px 0" },
}));

const BottomActions = styled(Box)(({ theme }) => ({
  display: "flex", gap: 16, paddingTop: "8px",
  borderTop: "2px solid #E2E8F0", marginTop: "auto",
  [theme.breakpoints.down("sm")]: {
    gap: 12,
    paddingTop: "6px",
  },
  [theme.breakpoints.down(400)]: {
    gap: 8,
    paddingTop: "4px",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  flex: 1, height: 56, borderRadius: 16, textTransform: "lowercase",
  fontSize: "1rem", fontWeight: 700,
  "&.primary": {
    background: "linear-gradient(135deg, #6200EE 0%, #7C4DFF 100%)",
    color: "white",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 35px -8px rgba(98, 0, 238, 0.6)",
    },
  },
  "&.secondary": {
    background: "#F2E9FF", color: "#6200EE",
    border: "2px solid #E2E8F0",
    "&:hover": {
      backgroundColor: "#6200EE", color: "white",
      transform: "translateY(-2px)",
    },
  },
  [theme.breakpoints.down("sm")]: {
    height: 48,
    borderRadius: 12,
    fontSize: "0.9rem",
  },
  [theme.breakpoints.down(400)]: {
    height: 44,
    borderRadius: 10,
    fontSize: "0.8rem",
  },
}));

const MenuDialog = ({
  open,
  onClose,
  businessLocationId,
  conversationId,
  onItemClick,
  onPlaceOrder,
  onCartClick,
  menuData,
  menuLoading = false,
  refetchMenuData,
}) => {
  const theme = useTheme();
  const dialogRef = useRef(null);
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(0);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [cartData, setCartData] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 100, y: 100 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  // Drag and resize functionality
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        // Keep dialog within viewport bounds
        const maxX = window.innerWidth - 400;
        const maxY = window.innerHeight - 300;
        setDialogPosition({
          x: Math.max(0, Math.min(e.clientX - dragOffset.x, maxX)),
          y: Math.max(0, Math.min(e.clientY - dragOffset.y, maxY)),
        });
      } else if (isResizing && dialogRef.current) {
        const rect = dialogRef.current.getBoundingClientRect();
        const newWidth = e.clientX - rect.left;
        const newHeight = e.clientY - rect.top;

        if (newWidth > 400 && newWidth < window.innerWidth * 0.95) {
          dialogRef.current.style.width = `${newWidth}px`;
        }
        if (newHeight > 300 && newHeight < window.innerHeight * 0.95) {
          dialogRef.current.style.height = `${newHeight}px`;
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset]);

  // Resolve location id: support both populated object (._id) and raw string
  const locationId = typeof businessLocationId === "string"
    ? businessLocationId
    : businessLocationId?._id ?? businessLocationId;

  // Refetch menu data when locationId changes (e.g., after payment updates conversationInfo)
  const prevLocationIdRef = useRef(locationId);
  useEffect(() => {
    if (open && locationId && refetchMenuData && prevLocationIdRef.current !== locationId) {
      prevLocationIdRef.current = locationId;
      const timeoutId = setTimeout(() => refetchMenuData(), 300);
      return () => clearTimeout(timeoutId);
    }
    if (locationId) prevLocationIdRef.current = locationId;
  }, [locationId, open, refetchMenuData]);

  // Fetch cart data
  const fetchCartData = async (convId) => {
    if (!convId) return;
    setCartLoading(true);
    try {
      const url = locationId
        ? `${ApiEndPoints.CART.get}/${convId}?locationId=${locationId}`
        : `${ApiEndPoints.CART.get}/${convId}`;
      const response = await axiosInstance.get(url);
      setCartData(response.data);
    } catch (error) {
      setCartData(null);
    } finally {
      setCartLoading(false);
    }
  };

  // Initialize dialog when opened (menu fetches once via useQuery enabled: open && locationId)
  useEffect(() => {
    if (open) {
      setIsInitializing(true);
      setSelectedMenuIndex(0);
      setSelectedCategoryIndex(0);

      // Center the dialog on screen with responsive dimensions
      const isMobile = window.innerWidth < 960; // md breakpoint
      const dialogWidth = isMobile ? window.innerWidth * 0.9 : 900;
      const dialogHeight = isMobile ? window.innerHeight * 0.8 : 700;
      const centerX = (window.innerWidth - dialogWidth) / 2;
      const centerY = (window.innerHeight - dialogHeight) / 2;

      setDialogPosition({
        x: Math.max(0, centerX), // Ensure it doesn't go off-screen
        y: Math.max(0, centerY),
      });

      setTimeout(() => {
        setIsInitializing(false);
        if (conversationId) fetchCartData(conversationId);
      }, 300);
    } else {
      setCartData(null);
      setIsInitializing(false);
      setSearchQuery("");
    }
  }, [open, locationId, conversationId]);

  // Event handlers
  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  const handleMenuTabChange = (event, newValue) => {
    setSelectedMenuIndex(newValue);
    setSelectedCategoryIndex(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Cart dialog handlers
  const handleOpenCartDialog = () => {
    if (onCartClick) {
      onCartClick(); // Use the passed cart click handler from parent
    }
  };


  // Helper function to check if item has stock for current location
  const hasStockForLocation = (item) => {
    if (!locationId) return true; // Show all if no location specified
    
    // For variant-enabled items
    if (item.variantEnabled && item.variants?.length > 0) {
      // Check if at least one variant has stock for current location
      return item.variants.some(variant => {
        if (!variant.perLocationStock?.length > 0) return false;
        return variant.perLocationStock.some(
          stock => stock.locationId === locationId
        );
      });
    }
    
    // For non-variant items
    if (item.locationStock?.length > 0) {
      return item.locationStock.some(
        stock => stock.locationId === locationId
      );
    }
    
    // If no location stock data, don't show the item
    return false;
  };

  // Filter menus based on schedule availability
  // API already returns both menus (isCatalog: false) and catalogs (isCatalog: true) together
  const allItems = getCurrentlyOpenMenus(menuData?.data?.menus, locationId);

  // Reset selected menu index if it's out of bounds after filtering
  useEffect(() => {
    if (allItems.length > 0 && selectedMenuIndex >= allItems.length) {
      setSelectedMenuIndex(0);
      setSelectedCategoryIndex(0);
    }
  }, [allItems.length, selectedMenuIndex]);

  // Data getters - simplified
  const currentMenu = allItems?.[selectedMenuIndex];
  const currentCategories = currentMenu?.categories || [];
  const currentCategory = currentCategories[selectedCategoryIndex];
  const allMenuItems = currentCategory?.menuItemIds || [];
  
  // Filter menu items based on location stock and search query
  const currentMenuItems = allMenuItems.filter(item => {
    // First check if item has stock for current location
    if (!hasStockForLocation(item)) return false;
    
    // Then filter by search query if provided
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.code?.toLowerCase().includes(query) ||
      item.dietTypes?.some(diet => diet.name?.toLowerCase().includes(query))
    );
  });

  // Currency: from menu item locationStock, never hardcode USD/$
  const getCurrencySymbol = (currencyCode, fallbackSymbol) => {
    if (!currencyCode || !currencyData?.currencies) return fallbackSymbol ?? "";
    const currency = currencyData.currencies.find(
      (curr) => curr.code === currencyCode.toUpperCase()
    );
    return currency ? currency.symbol : (fallbackSymbol ?? "");
  };

  const formatPrice = (price, currencyCode) => {
    if (price === null || price === undefined) {
      return "price not available";
    }
    const symbol = getCurrencySymbol(currencyCode);
    const formattedPrice = typeof price === "number" ? price.toFixed(2) : parseFloat(price || 0).toFixed(2);
    return `${symbol}${formattedPrice}`;
  };
  
  const formatTime = (minutes) => `${minutes} mins`;
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return imagePath.startsWith("http")
      ? imagePath
      : `${IMAGE_URL}${imagePath}`;
  };

  // Get price and currency for a menu item based on location
  const getItemPriceAndCurrency = (item) => {
    if (!item) return { price: null, currency: "" };

    // For variant-enabled items
    if (item.variantEnabled && item.variants?.length > 0) {
      const firstVariant = item.variants[0];
      
      // Try to find price for current location
      if (locationId && firstVariant.perLocationStock?.length > 0) {
        const locationStock = firstVariant.perLocationStock.find(
          (stock) => stock.locationId === locationId
        );
        
        if (locationStock?.price !== undefined && locationStock?.currency) {
          return {
            price: locationStock.price,
            currency: locationStock.currency,
          };
        }
      }
      
      // Fallback to first available location stock
      if (firstVariant.perLocationStock?.length > 0) {
        const firstStock = firstVariant.perLocationStock[0];
        if (firstStock?.price !== undefined && firstStock?.currency) {
          return {
            price: firstStock.price,
            currency: firstStock.currency,
          };
        }
      }
    }
    
    // For non-variant items
    // Try to find price for current location
    if (locationId && item.locationStock?.length > 0) {
      const locationStock = item.locationStock.find(
        (stock) => stock.locationId === locationId
      );
      
      if (locationStock?.price !== undefined && locationStock?.currency) {
        return {
          price: locationStock.price,
          currency: locationStock.currency,
        };
      }
    }
    
    // Fallback to first available location stock
    if (item.locationStock?.length > 0) {
      const firstStock = item.locationStock[0];
      if (firstStock?.price !== undefined && firstStock?.currency) {
        return {
          price: firstStock.price,
          currency: firstStock.currency,
        };
      }
    }
    
    // Final fallback
    return {
      price: item.price || null,
      currency: item.currency || "",
    };
  };

  // Calculate item quantity from cart data
  const getItemQuantity = (menuItemId) => {
    if (!cartData?.data?.cart?.cart) return 0;
    
    const cartItem = cartData.data.cart.cart.find(item => 
      item && item.menuItemId?._id === menuItemId
    );
    
    if (!cartItem) return 0;
    
    if (cartItem.variantEnabled && cartItem.variants) {
      // For variant items, sum up all quantities from all variants
      return cartItem.variants.reduce((total, variant) => {
        return total + variant.items.reduce((variantTotal, item) => {
          return variantTotal + item.quantity;
        }, 0);
      }, 0);
    } else {
      // For single items, return the quantity directly
      return cartItem.quantity || 0;
    }
  };

  // Calculate total cart counts
  const getCartSummary = () => {
    if (!cartData?.data?.cart) {
      return { totalItems: 0, totalQuantity: 0, totalAmount: 0 };
    }
    
    return {
      totalItems: cartData.data.cart.totalItems || 0,
      totalQuantity: cartData.data.cart.totalQuantity || 0,
      totalAmount: cartData.data.cart.totalAmount || 0,
    };
  };

  // Menu item renderer
  const renderMenuItem = (item, index) => {
    const imageUrl = getImageUrl(item.imageVideo?.[0]);
    const { price, currency } = getItemPriceAndCurrency(item);
    const itemQuantity = getItemQuantity(item._id);

    return (
      <MenuItemCard key={item._id || index}>
        <MenuItemContent>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                  color: "#1E293B",
                  lineHeight: 1.3,
                  textTransform: "lowercase",
                }}
              >
                {item.name}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.2rem", sm: "1.3rem", md: "1.5rem" },
                  color: primaryColor,
                  ml: { xs: 1, sm: 2 },
                }}
              >
                {formatPrice(price, currency)}
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: "#64748B",
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
                lineHeight: 1.6,
                textTransform: "lowercase",
              }}
            >
              {item.description}
            </Typography>
            {/* Prep time (frying pan icon) + Wait time (clock icon) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5 },
                flexWrap: "wrap",
                mb: 1,
              }}
            >
              {(item.prepTime != null && item.prepTime > 0) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "#64748B",
                    fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                    fontWeight: 500,
                  }}
                >
                  <img
                    src={prepTimeIcon}
                    alt="Prep time"
                    style={{
                      width: 24,
                      height: 18,
                      objectFit: "contain",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ textTransform: "lowercase", fontSize: "inherit" }}
                  >
                    {formatTime(item.prepTime)}
                  </Typography>
                </Box>
              )}
              {(item.prepTime != null && item.prepTime > 0) && (item.waitTime != null && item.waitTime > 0) && (
                <Box
                  sx={{
                    width: "1px",
                    height: 14,
                    backgroundColor: "#E2E8F0",
                    borderRadius: 1,
                  }}
                />
              )}
              {(item.waitTime != null && item.waitTime > 0) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "#64748B",
                    fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                    fontWeight: 500,
                  }}
                >
                  <TimeIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  <Typography
                    variant="body2"
                    sx={{ textTransform: "lowercase", fontSize: "inherit" }}
                  >
                    {formatTime(item.waitTime)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Diet type icons below the information */}
            {item.dietTypes?.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                }}
              >
                {item.dietTypes.map((diet, dietIndex) => (
                  diet.icon ? (
                    <Box
                      key={diet._id || dietIndex}
                      component="img"
                      src={diet.icon}
                      alt={diet.name}
                      title={diet.name}
                      sx={{
                        width: 28,
                        height: 24,
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Chip
                      key={diet._id || dietIndex}
                      label={diet.name}
                      size="small"
                      sx={{
                        height: { xs: 24, sm: 28 },
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        fontWeight: 600,
                        backgroundColor: "#F0F9FF",
                        color: "#0369A1",
                        border: "1px solid #BAE6FD",
                        textTransform: "lowercase",
                      }}
                    />
                  )
                ))}
              </Box>
            )}
          </Box>
          <ItemImageContainer>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={item.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
                }}
              >
                🍽️
              </Box>
            )}
            {itemQuantity > 0 ? (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 4,
                  right: 4,
                  minWidth: {md:36,xs:28},
                  height: {md:36,xs:28},
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: {md:"0.9rem",xs:"0.8rem"},
                  fontWeight: 700,
                  boxShadow: "0 4px 12px -2px rgba(16, 185, 129, 0.4)",
                  transition: commonTransition,
                  "&:hover": {
                    transform: "scale(1.1)",
                    boxShadow: "0 8px 20px -4px rgba(16, 185, 129, 0.6)",
                    cursor: "pointer",
                  },
                }}
                onClick={() => onItemClick && onItemClick(item, null)}
              >
                {itemQuantity}+
              </Box>
            ) : (
              <AddButton
                size="small"
                onClick={() => onItemClick && onItemClick(item, null)}
              >
                <AddIcon /> 
              </AddButton>
            )}
          </ItemImageContainer>
        </MenuItemContent>
      </MenuItemCard>
    );
  };

  if (!open) return null;

  return (
    <CustomOverlay>
      <CustomDialog
        ref={dialogRef}
        style={{ left: dialogPosition.x, top: dialogPosition.y }}
      >
        <DialogHeader onMouseDown={handleDragStart}>
          <DragHandle />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.25rem" },
              textTransform: "lowercase",
              ml: { xs: 1, sm: 2 },
            }}
          >
            {currentMenu?.isCatalog === true ? "catalog" : "menu"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Cart Icon */}
            <IconButton
              onClick={handleOpenCartDialog}
              size={theme.breakpoints.down("sm") ? "small" : "medium"}
              sx={{
                color: primaryColor,
                backgroundColor: primaryLight,
                "&:hover": {
                  backgroundColor: primaryColor,
                  color: "white",
                  transform: "scale(1.05)",
                },
                transition: commonTransition,
                position: "relative",
              }}
            >
              <CartIcon
                fontSize={theme.breakpoints.down("sm") ? "small" : "medium"}
              />
              {/* Cart Badge */}
              {getCartSummary().totalItems > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -2,
                    right: -2,
                    minWidth: 18,
                    height: 18,
                    borderRadius: "50%",
                    backgroundColor: "#DC2626",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    border: "2px solid white",
                  }}
                >
                  {getCartSummary().totalItems}
                </Box>
              )}
            </IconButton>
            
            {/* Close Icon */}
            <IconButton
              onClick={onClose}
              size={theme.breakpoints.down("sm") ? "small" : "medium"}
              sx={{
                color: primaryColor,
                backgroundColor: primaryLight,
                "&:hover": {
                  backgroundColor: primaryColor,
                  color: "white",
                  transform: "scale(1.05)",
                },
                transition: commonTransition,
              }}
            >
              <CloseIcon
                fontSize={theme.breakpoints.down("sm") ? "small" : "medium"}
              />
            </IconButton>
          </Box>
        </DialogHeader>

        <ContentContainer>
          {/* Loading Overlay */}
          {(isInitializing || menuLoading || cartLoading) && (
            <Box sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}>
              <CircularProgress
                size={60}
                sx={{ color: primaryColor }}
              />
            </Box>
          )}
          
          {allItems.length > 0 ? (
            <ScrollableContainer>
              {/* Hero Section - scrolls with content */}
              <HeroSection backgroundImage={getImageUrl(currentMenu?.image)}>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      mb: { xs: 0.5, sm: 1 },
                      fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
                      textTransform: "lowercase",
                      textShadow: "0 2px 4px rgba(255,255,255,0.2)",
                      color: "#fff",
                    }}
                  >
                    {currentMenu?.locationIds?.[0]?.businessLocationName ||
                      "restaurant"}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      opacity: 0.9,
                      fontWeight: 600,
                      fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                      textTransform: "lowercase",
                      textShadow: "0 1px 2px rgba(255,255,255,0.1)",
                      color: "#fff",
                    }}
                  >
                    {currentMenu?.name}
                  </Typography>
                </Box>
              </HeroSection>

              {/* Sticky Header - Search, Menu Tabs and Category Chips */}
              <StickyHeader>
                {/* Search Field */}
                <SearchContainer>
                  <SearchField
                    fullWidth
                    placeholder={currentMenu?.isCatalog === true ? "search catalog items..." : "search menu items..."}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#64748B", fontSize: "1.2rem" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            sx={{ 
                              color: "#64748B",
                              "&:hover": { color: "#6200EE" }
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </SearchContainer>

                {/* Multiple menu tabs */}
                {allItems.length > 1 && (
                  <MenuTabs
                    value={selectedMenuIndex}
                    onChange={handleMenuTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {allItems.map((menu, index) => (
                      <Tab key={menu._id || index} label={menu.name} />
                    ))}
                  </MenuTabs>
                )}

                {/* Category chips */}
                {currentCategories.length > 0 && (
                  <CategoryChips>
                    {currentCategories.map((category, index) => (
                      <CategoryChip
                        key={category._id || index}
                        label={category.name}
                        active={selectedCategoryIndex === index}
                        onClick={() => setSelectedCategoryIndex(index)}
                      />
                    ))}
                  </CategoryChips>
                )}
              </StickyHeader>

              {/* Menu items - scrolls under sticky header */}
              <MenuItemsContainer>
                {currentMenuItems.length > 0 ? (
                  currentMenuItems.map(renderMenuItem)
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: { xs: 6, sm: 8 },
                      color: "#64748B",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                        textTransform: "lowercase",
                      }}
                    >
                      {searchQuery.trim() 
                        ? `no items found for "${searchQuery}"` 
                        : "no items available in this category"
                      }
                    </Typography>
                  </Box>
                )}
              </MenuItemsContainer>
            </ScrollableContainer>
          ) : !isInitializing && !menuLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#64748B",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                  textTransform: "lowercase",
                }}
              >
                {!locationId
                  ? "business location not available. cannot load menu."
                  : "no menu available for this location"}
              </Typography>
            </Box>
          ) : null}

        {/* Order Actions - Outside scrollable area */}
        <BottomActions>
          <ActionButton 
            className="secondary"
            onClick={handleOpenCartDialog}
          >
            view items ({getCartSummary().totalItems})
          </ActionButton>
        </BottomActions>
        </ContentContainer>


        <ResizeHandle onMouseDown={handleResizeStart} />
      </CustomDialog>

    </CustomOverlay>
  );
};

export default MenuDialog;
